import { useCallback, useMemo, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import moment from 'moment-timezone';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { useApi } from '@/lib/legacy';
import { notifyError, notifySuccess } from '@/lib/notify';
import { alpineTheme } from '../../ui/components/Grid/themes';
import { usePolicyPageContext as usePolicyPageContextJs } from './context/PolicyPageContext';
import type { Row } from './tabParts';

type PolicyPageState = { endorsements?: Row[]; policy: Row; MCEsData?: Row[] };
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: pages/PolicyPage/context/PolicyPageContext.jsx
const usePolicyPageContext = usePolicyPageContextJs as any as () => PolicyPageState;
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: AG Grid event params
type GridEvent = any;

const formatTitle = (text: string) => text.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());

function formatDate(dateString?: string) {
  if (!dateString) return undefined;
  const [year, month, day] = dateString.split('-');
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: '2-digit' }).toUpperCase();
}

/** The policy's forms in print order: reorder them, save the order, download the selected ones. */
export const PolicyGeneration = () => {
  const { endorsements = [], policy, MCEsData = [] } = usePolicyPageContext();
  const { downloadPolicyWithEndorsements, updateOrdinals } = useApi();
  const [isDownloading, setIsDownloading] = useState(false);
  const [savingOrders, setSavingOrders] = useState(false);
  const [rowData, setRowData] = useState<Row[]>([]);
  const [selectedForms, setSelectedForms] = useState<Row[]>([]);

  const validEndorsements = endorsements.filter((e) => e?.endorsementType && e.endorsementType !== 'mce');
  const groupedEndorsements = validEndorsements.reduce<Record<string, Row[]>>((acc, endorsement) => {
    const key = endorsement.endorsementType;
    if (!acc[key]) acc[key] = [];
    acc[key].push(endorsement);
    return acc;
  }, {});

  const getName = ({ endorsementType, endorsementData, notes, subIdx }: Row) => {
    if (['highValueVehicle', 'lenderLossPayee', 'temporaryLocations'].some((type) => endorsementType?.includes(type)))
      return `${formatTitle(endorsementType)} ${subIdx + 1}`;
    if (endorsementType === 'MceForm')
      return `MISCELLANEOUS CHANGE ENDORSEMENT -\nINVENTORY SCHEDULE FLUCTUATION -\n${formatDate(endorsementData?.effectiveAt)} - ${formatDate(
        endorsementData?.effectiveUntil || moment(policy.effectiveUntil).format('YYYY-MM-DD')
      )}`;
    return notes || `${endorsementType} ${subIdx + 1}`;
  };

  const allEndorsements = Object.values(groupedEndorsements).flatMap((group) =>
    group.map((endorsement, subIdx): Row => ({ ...endorsement, subIdx }))
  );
  const initialRowData = useMemo(
    () =>
      allEndorsements.map(({ ordinal, endorsementType, formId, endorsementData, notes, rows, subIdx, policyFormId }, index) => {
        let mceNumber = '';
        if (endorsementType === 'MceForm') {
          mceNumber = notes || '';
        } else if (endorsementType === 'invEndorsement' && endorsementData?.parent) {
          const parent = endorsementData.parent;
          if (parent.type === 'policy') {
            mceNumber = 'Policy';
          } else if (parent.type === 'mce' && parent.id) {
            const mce = MCEsData.find((m) => String(m.id) === String(parent.id));
            mceNumber = mce?.mceNumber || `MCE ${parent.id}`;
          }
        }
        return {
          ordinal: ordinal || index + 1,
          id: policyFormId,
          name: getName({ endorsementType, formId, endorsementData, subIdx, notes }),
          mceNumber,
          formId,
          ordinalChanged: false,
          ...(endorsementData && { endorsementData }),
          endorsementType,
          ...(notes && { notes }),
          ...(rows && { rows }),
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allEndorsements, MCEsData]
  );

  // Today's rows are taken once, on the first render (a useState initializer), and kept as edited.
  useState(() => {
    setRowData(initialRowData);
  });

  const onSelectionChanged = useCallback((params: GridEvent) => {
    setSelectedForms(params.api.getSelectedNodes().map((node: GridEvent) => node.data));
  }, []);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const payload = selectedForms
        .map(({ ordinal, endorsementData, endorsementType, formId, notes, rows }) => ({
          ordinal,
          endorsementType,
          formId,
          ...(endorsementData && { endorsementData }),
          ...(notes && { notes }),
          ...(rows && { rows }),
        }))
        .sort((a, b) => Number(a.ordinal) - Number(b.ordinal));
      const { data } = await downloadPolicyWithEndorsements(policy.id, payload);
      if (data?.value) window.open(data.value, '_blank');
    } catch {
      notifyError('Unable to download, please try again later.');
    } finally {
      setIsDownloading(false);
    }
  };

  const clearRowStyles = () => {
    document.querySelectorAll<HTMLElement>('.ag-row').forEach((row) => {
      row.style.borderTop = '';
      row.style.backgroundColor = '';
      row.style.opacity = '';
    });
  };

  const onRowDragMove = (event: GridEvent) => {
    document.querySelectorAll<HTMLElement>('.ag-row').forEach((row) => {
      row.style.borderTop = '';
    });
    const dragged = event.node?.data;
    if (!dragged || dragged.endorsementType === 'PolicyForm') return;
    const mouseY = event.event?.clientY;
    if (!mouseY) return;

    const rows = Array.from(document.querySelectorAll<HTMLElement>('.ag-row'));
    let targetRow: HTMLElement | null = null;
    for (const row of rows) {
      const rect = row.getBoundingClientRect();
      if (mouseY >= rect.top && mouseY <= rect.bottom) {
        targetRow = row;
        break;
      }
    }
    if (!targetRow && rows.length > 0) {
      targetRow = rows[rows.length - 1] as HTMLElement;
    }
    if (!targetRow) return;

    const node = event.api.getDisplayedRowAtIndex(Number(targetRow.getAttribute('row-index')));
    if (node?.data?.endorsementType === 'PolicyForm') {
      const allNodes: GridEvent[] = [];
      event.api.forEachNode((n: GridEvent) => allNodes.push(n));
      const nextValid = allNodes.find((n) => n.rowIndex > node.rowIndex && n.data?.endorsementType !== 'PolicyForm');
      if (nextValid) {
        targetRow = document.querySelector<HTMLElement>(`[row-index='${nextValid.rowIndex}']`);
      }
    }
    if (targetRow) {
      targetRow.style.borderTop = '3px solid var(--drag-target)';
    }
  };

  const handleSaveOrder = async () => {
    setSavingOrders(true);
    try {
      const payload = rowData
        .filter((row) => row.ordinalChanged)
        .map(({ id, formId, ordinal }) => ({ policyFormId: id, formId, newOrdinal: ordinal }));
      await updateOrdinals(policy.id, payload);
      notifySuccess('Saved Successfully ');
    } catch {
      notifyError('Unable to Save, please try again later.');
    } finally {
      setSavingOrders(false);
    }
  };

  const columnDefs = useMemo(
    () => [
      { headerName: '', width: 50, field: 'drag', rowDrag: (params: GridEvent) => params.data?.endorsementType !== 'PolicyForm' },
      {
        headerName: 'Order',
        field: 'ordinal',
        width: 125,
        sortable: true,
        sort: 'asc' as const,
        comparator: (valueA: unknown, valueB: unknown) => Number(valueA) - Number(valueB),
        cellClassRules: { 'ag-cell-editable': (params: GridEvent) => params.data?.endorsementType !== 'PolicyForm' },
      },
      { headerName: 'Name', field: 'name', minWidth: 850 },
      { field: 'mceNumber', headerName: 'MCE Number', flex: 1, minWidth: 300 },
    ],
    []
  );

  const defaultColDef = useMemo(() => ({ sortable: true, resizable: true }), []);
  const rowSelection = useMemo(() => ({ mode: 'multiRow' as const, enableClickSelection: true }), []);
  const selectionColumnDef = useMemo(() => ({ width: 50 }), []);

  const onGridReady = useCallback((params: GridEvent) => {
    params.api.forEachNode((node: GridEvent) => node.setSelected(true));
  }, []);

  const onRowDragEnter = (event: GridEvent) => {
    const rowElement = document.querySelector<HTMLElement>(`[row-index='${event.node.rowIndex}']`);
    if (rowElement) {
      rowElement.style.backgroundColor = 'var(--drag-source)';
      rowElement.style.opacity = '0.7';
    }
  };

  const onRowDragEnd = (event: GridEvent) => {
    const movingNode = event.node;
    const overNode = event.overNode;
    const gridApi = event.api;
    const rowNeedsToMove = movingNode !== overNode;

    const rowElement = document.querySelector<HTMLElement>(`[row-index='${movingNode.rowIndex}']`);
    if (rowElement) {
      rowElement.style.backgroundColor = '';
      rowElement.style.opacity = '';
    }
    clearRowStyles();

    const newRowData: Row[] = [];
    gridApi.forEachNode((node: GridEvent) => newRowData.push(node.data));

    if (rowNeedsToMove) {
      const movedItem = movingNode.data;
      const overData = overNode.data;
      if (overData?.endorsementType === 'PolicyForm' || movedItem?.endorsementType === 'PolicyForm') {
        return;
      }
      const fromOrdinal = movedItem.ordinal;
      const toOrdinal = overData.ordinal;
      const rangeStart = Math.min(fromOrdinal, toOrdinal);
      const rangeEnd = Math.max(fromOrdinal, toOrdinal);

      const affectedRows = newRowData.filter((row) => row.ordinal >= rangeStart && row.ordinal <= rangeEnd);
      const remainingRows = newRowData.filter((row) => row.ordinal < rangeStart || row.ordinal > rangeEnd);
      const updatedAffectedRows = affectedRows.filter((row) => row.ordinal !== fromOrdinal);
      const movedRowIndex = updatedAffectedRows.findIndex((row) => row.ordinal >= toOrdinal);
      if (movedRowIndex === -1) {
        updatedAffectedRows.push(movedItem);
      } else {
        updatedAffectedRows.splice(movedRowIndex, 0, movedItem);
      }
      const affectedOrdinals = updatedAffectedRows.map((row) => row.ordinal).sort((a, b) => a - b);
      updatedAffectedRows.forEach((row, index) => {
        row.ordinal = affectedOrdinals[index];
        row.ordinalChanged = true;
      });
      const finalRowData = [...remainingRows, ...updatedAffectedRows].sort((a, b) => a.ordinal - b.ordinal);
      setRowData(finalRowData);
      setTimeout(() => {
        gridApi.ensureIndexVisible(0);
        gridApi.ensureIndexVisible(
          finalRowData.findIndex((row) => row.ordinal === movedItem.ordinal),
          'top'
        );
      }, 0);
      event.api.refreshCells({ force: true });
    }
  };

  return (
    <div className="h-[500px] w-full">
      <AgGridReact
        theme={alpineTheme}
        columnMenu="legacy"
        rowData={rowData}
        columnDefs={columnDefs}
        rowSelection={rowSelection}
        selectionColumnDef={selectionColumnDef}
        onSelectionChanged={onSelectionChanged}
        defaultColDef={defaultColDef}
        onGridReady={onGridReady}
        onRowDragEnter={onRowDragEnter}
        onRowDragEnd={onRowDragEnd}
        suppressMoveWhenRowDragging={true}
        onRowDragLeave={clearRowStyles}
        onRowDragMove={onRowDragMove}
      />
      <div className="flex flex-row">
        <Button className="mt-4 ml-2 w-[150px] rounded-lg" onClick={handleSaveOrder} disabled={savingOrders}>
          {savingOrders ? <Spinner /> : null}
          Save Order
        </Button>
        <Button className="mt-4 ml-2 w-[150px] rounded-lg" onClick={handleDownload} disabled={isDownloading}>
          {isDownloading ? <Spinner /> : null}
          Download
        </Button>
      </div>
    </div>
  );
};

export default PolicyGeneration;
